import { Formik } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { useAlert } from '../../../AlertProvider';
import * as Yup from 'yup';

import { Box, Divider, FormHelperText, Typography } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { useQueryClient } from '@tanstack/react-query';
import { ActionItemKeys } from '@uplift/api';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { useNotificationPreferencesMutation, userContactsQueryKey } from '@uplift/queries';

import { Button } from '../../../Button';
import { SubmitButton } from '../../../Form';
import { CodeInput } from '../../../Inputs/CodeInput';
import { ModalActions, ModalContent, ModalTitle } from '../../../Modal';
import { PhoneMask } from '../../../Utils';
import { AppointmentRemindersDisclosure, TextAlertDisclosure } from '../../../TextAlertDisclosure';
import { useVerifyContact } from './PhoneVerificationModal.hooks';

const PHONE_REGEX = /\(\d{3}\) ?\d{3}-(?<lastfour>\d{4})/;

export type VerificationFormProps = {
  contactId: string;
  open: boolean;
  phone: string;
  userId: string;
  disableEdit?: boolean;
  loading?: boolean;
  maskPhone?: boolean;
  showAppointmentSmsOptIn?: boolean;
  successText?: string;
  title?: string | null;
  onClose?: () => void;
  onEditMobileNumber?: () => void;
  onSendNewCode?: () => void;
  onSubmit?: () => void;
};

const ButtonTypography = withStyles(Button, {
  root: {
    textTransform: 'none',
    padding: 0,
    borderLeft: 0,
    lineHeight: 1,
    fontSize: '14px',

    '&:hover': {
      backgroundColor: 'inherit',
    }
  }
});


const validationSchema = Yup.object().shape({
  code: Yup.string().length(4).required(),
});

export const VerificationForm: React.FC<VerificationFormProps> = (props) => {
  const queryClient = useQueryClient();
  const l10n = useL10n();
  const alert = useAlert();
  const isMobile = useIsMobile();
  const isDesktop = !isMobile;
  const { verifyContact, error: verifyContactError } = useVerifyContact();
  const { mutateAsync: updateNotificationPreferences } = useNotificationPreferencesMutation(props.userId);
  const helperErrorMessage = verifyContactError?.error
    ? l10n.phoneValidationModal.errors[verifyContactError.error]
    : undefined;

  const phoneDisplay = useMemo(() => {
    if (props.maskPhone) {
      const phoneMatch = props.phone.match(PHONE_REGEX);

      if (!phoneMatch?.groups) {
        return props.phone;
      }

      return (
        <span>
          <PhoneMask phone={props.phone} />
        </span>
      );
    }

    return props.phone;
  }, [ props.maskPhone, props.phone ]);

  const title = props.title ?? l10n.phoneValidationModal.verifyYourMobileNumberToReceiveTextReminders;

  const handleSubmit = useCallback(async (values) => {
    await verifyContact({
      contactId: props.contactId,
      verificationCode: values.code,
    });

    if (props.showAppointmentSmsOptIn) {
      await updateNotificationPreferences({ appointment_sms: values.appointmentSmsOptIn });
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: userContactsQueryKey(props.userId) }),
      queryClient.invalidateQueries({ queryKey: [ 'actionItem', ActionItemKeys.CLIENT_SMS_VERIFY ] })
    ]);

    props.onClose?.();
    props.onSubmit?.();

    const defaultSuccessText = values.appointmentSmsOptIn
      ? l10n.phoneValidationModal.textRemindersOn
      : l10n.phoneValidationModal.mobileNumberVerified;

    alert.success(<strong>{props.successText ?? defaultSuccessText}</strong>);
  }, [ alert, l10n.phoneValidationModal.mobileNumberVerified, l10n.phoneValidationModal.textRemindersOn, props, queryClient, updateNotificationPreferences, verifyContact ]);

  return (
    <Formik
      validateOnMount
      validationSchema={validationSchema}
      enableReinitialize
      initialErrors={{
        code: '',
      }}
      initialValues={{
        contactId: props.contactId,
        phone: props.phone,
        code: '',
        appointmentSmsOptIn: false,
      }}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <>
          {title && (
            <ModalTitle onClose={props.onClose}>
              {isDesktop && title}&nbsp;
            </ModalTitle>
          )}
          <ModalContent>
            {(isMobile && title) && (
              <Typography variant="h3">
                {title}
              </Typography>
            )}
            <Box mt={3}>
              <Typography variant="body1" style={{ whiteSpace: 'nowrap' }}>
                {l10n.phoneValidationModal.enterCodeTextedTo} <strong>{phoneDisplay}</strong>
              </Typography>
            </Box>
            {!props.disableEdit && props.onEditMobileNumber && (
              <Box mt={1}>
                <ButtonTypography size="small" color="secondary" onClick={props.onEditMobileNumber}>
                  {l10n.phoneValidationModal.editMobileNumber}
                </ButtonTypography>
              </Box>
            )}
            <Box mt={10}>
              <CodeInput
                name="code"
                codeLength={4}
                onSubmit={formik.handleSubmit}
                autoFocus
              />
            </Box>
            <Box mt={2}>
              <FormHelperText error={!!helperErrorMessage}>
                {helperErrorMessage || l10n.phoneValidationModal.yourCodeWillExpire}
              </FormHelperText>
            </Box>
            {isMobile && (
              <Box mt={4}>
                <ButtonTypography size="small" color="secondary" loading={props.loading} onClick={props.onSendNewCode}>
                  {l10n.phoneValidationModal.sendNewCode}
                </ButtonTypography>
              </Box>
            )}
            <Box mt={2}>
              {props.showAppointmentSmsOptIn
                ? <AppointmentRemindersDisclosure />
                : <TextAlertDisclosure />}
            </Box>
          </ModalContent>
          {isMobile && <Divider />}
          <ModalActions>
            <Box display="flex" justifyContent="space-between" width="100%">
              {isDesktop && (
                <Button
                  onClick={props.onSendNewCode}
                  loading={props.loading}
                  disabled={formik.isSubmitting}
                  color="secondary"
                >
                  {l10n.phoneValidationModal.sendNewCode}
                </Button>
              )}
              <SubmitButton size={isMobile ? 'large' : 'medium'} fullWidth={isMobile} variant="contained" color="secondary">
                {l10n.phoneValidationModal.verifyMobileNumber}
              </SubmitButton>
            </Box>
          </ModalActions>
        </>
      )}
    </Formik>
  );

};
